// sparepart.repo — akses Supabase: Supplier + Sparepart master, Request/Serah/Modal, Hutang.
import { supabase, pat } from '@core/supabase';
import { fetchAllPaged, fetchAllChunked } from '@core/paging';
import { rejectedLoss, serahTotal, type SparepartReq, type Serah, type SerahItem } from '@domain/sparepart';

export interface Supplier { id: number; nama: string; wa: string | null; kota: string | null; alamat: string | null; rekening: string | null; outlet: string | null; aktif: boolean;
  /** Kapan supplier terakhir menagih lewat portalnya (0118) — ditampilkan di rekap Hutang Supplier. */
  tagih_at?: string | null }
export interface Sparepart { id: number; supplier_id: number; barcode: string | null; nama: string; kategori: string | null; stok: number; harga: number }

// ---------- Supplier master ----------
export async function listSuppliers(): Promise<Supplier[]> {
  const { data, error } = await supabase().from('suppliers').select('id,nama,wa,kota,alamat,rekening,outlet,aktif,tagih_at').order('nama');
  if (error) throw new Error(error.message);
  return (data ?? []) as Supplier[];
}
export async function createSupplier(d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('suppliers').insert(d); if (error) throw new Error(error.message);
}
export async function updateSupplier(id: number, d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('suppliers').update(d).eq('id', id); if (error) throw new Error(error.message);
}
export async function removeSupplier(id: number): Promise<void> {
  const { error } = await supabase().from('suppliers').delete().eq('id', id); if (error) throw new Error(error.message);
}
export async function listOutlets(): Promise<{ kode: string; nama: string }[]> {
  const { data, error } = await supabase().from('outlets').select('kode,nama').order('kode');
  if (error) throw new Error(error.message);
  return (data ?? []) as { kode: string; nama: string }[];
}
/** Jumlah request sparepart supplier (jejak hutang/serah) — cegah hapus supplier ber-riwayat keuangan. */
export async function requestCountForSupplier(supplierId: number): Promise<number> {
  const { count, error } = await supabase().from('sparepart_requests').select('id', { count: 'exact', head: true }).eq('supplier_id', supplierId);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

// ---------- Sparepart master ----------
// PENTING: PostgREST memotong hasil di 1.000 baris TANPA memberi tanda (terbukti: 1.349 baris di
// database, yang sampai ke aplikasi 1.000). Katalog impor bisa 10.000 SKU → semua daftar di sini
// wajib menyebut batasnya sendiri + tahu jumlah sebenarnya, supaya tak ada data yang hilang diam-diam.
export const LIST_MAX = 500;

/** Halaman pertama katalog (maks LIST_MAX). Untuk sisanya: pakai pencarian, atau allSpareparts(). */
export async function listSpareparts(supplierId?: number, limit = LIST_MAX): Promise<Sparepart[]> {
  let q = supabase().from('spareparts').select('id,supplier_id,barcode,nama,kategori,stok,harga').order('nama').limit(limit);
  if (supplierId) q = q.eq('supplier_id', supplierId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Sparepart[];
}

/** Jumlah sebenarnya di database (bukan yang tampil) — untuk memberi tahu kalau daftarnya dipotong. */
export async function countSpareparts(supplierId?: number): Promise<number> {
  let q = supabase().from('spareparts').select('id', { count: 'exact', head: true });
  if (supplierId) q = q.eq('supplier_id', supplierId);
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** SELURUH katalog, diambil per halaman. Dipakai export — hasil unduhan tak boleh terpotong diam-diam. */
export async function allSpareparts(supplierId?: number): Promise<Sparepart[]> {
  const PAGE = 1000; const out: Sparepart[] = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase().from('spareparts').select('id,supplier_id,barcode,nama,kategori,stok,harga').order('nama').range(from, from + PAGE - 1);
    if (supplierId) q = q.eq('supplier_id', supplierId);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Sparepart[];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}
/** Cari sparepart by NAMA atau BARCODE (case-insensitive). Utk serah/scan.
 *  Nilai dibungkus kutip-ganda + escape → koma/kurung diperlakukan literal (cegah filter-injection PostgREST). */
export async function searchSpareparts(term: string, supplierId?: number): Promise<Sparepart[]> {
  const t = term.trim();
  if (!t) return listSpareparts(supplierId);
  let q = supabase().from('spareparts').select('id,supplier_id,barcode,nama,kategori,stok,harga')
    .or(`nama.ilike.${pat(t)},barcode.ilike.${pat(t)}`).order('nama').limit(30);
  if (supplierId) q = q.eq('supplier_id', supplierId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Sparepart[];
}
/** Part dipakai (serah non-batal) per tiket → Map no → [{nama,qty}]. Utk Detail Laporan Teknisi (lazy-fetch). */
export async function usageForTickets(nos: string[]): Promise<Map<string, { nama: string; qty: number }[]>> {
  const m = new Map<string, { nama: string; qty: number }[]>();
  if (!nos.length) return m;
  const data = await fetchAllChunked<{ ticket_no: string; serah: unknown; batal: boolean | null }>(nos,
    (bagian, a, b) => supabase().from('sparepart_requests').select('ticket_no,serah,batal')
      .in('ticket_no', bagian).order('id').range(a, b));
  for (const r of data) {
    if (r.batal) continue;
    const serah = r.serah as { items?: { nama?: string; qty?: number }[] } | null;
    if (!serah?.items?.length) continue;
    const arr = m.get(r.ticket_no) ?? [];
    for (const it of serah.items) arr.push({ nama: it.nama ?? '—', qty: Number(it.qty) || 0 });
    m.set(r.ticket_no, arr);
  }
  return m;
}

/** Barcode per supplier (utk cek duplikat — barcode = kunci stok decrement_stock). */
export async function barcodesForSupplier(supplierId: number): Promise<{ id: number; barcode: string | null }[]> {
  const { data, error } = await supabase().from('spareparts').select('id,barcode').eq('supplier_id', supplierId);
  if (error) throw new Error(error.message);
  return (data ?? []) as { id: number; barcode: string | null }[];
}
export async function createSparepart(d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('spareparts').insert(d); if (error) throw new Error(error.message);
}
export async function updateSparepart(id: number, d: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().from('spareparts').update(d).eq('id', id); if (error) throw new Error(error.message);
}
export async function removeSparepart(id: number): Promise<void> {
  const { error } = await supabase().from('spareparts').delete().eq('id', id); if (error) throw new Error(error.message);
}

// ---------- Request / Serah / Modal ----------
const REQ_SEL = 'id,ticket_no,outlet,supplier_id,supplier_nama,items,serah,batal,indent,indent_eta,indent_note,foto,parent_req_id,titip_ket,tolak_ket,diterima_at,diterima_by,ingat_at,created_at,by_user,req_payments(amount,method,note,by_user,created_at)';
interface ReqRaw { id: number; ticket_no: string; outlet: string | null; supplier_id: number | null; supplier_nama: string | null; items: unknown; serah: unknown; batal: boolean | null; indent?: boolean | null; indent_eta?: string | null; indent_note?: string | null; foto?: unknown; created_at: string; by_user: string | null; req_payments?: { amount: number; method: string | null; note: string | null; by_user: string | null; created_at: string }[] }
const toReq = (r: ReqRaw): SparepartReq => ({
  id: r.id, ticket_no: r.ticket_no, outlet: r.outlet, supplier_id: r.supplier_id, supplier_nama: r.supplier_nama,
  items: Array.isArray(r.items) ? (r.items as string[]) : [], serah: (r.serah ?? null) as Serah | null, batal: r.batal ?? false, created_at: r.created_at, by_user: r.by_user ?? undefined,
  indent: r.indent ?? false, indent_eta: r.indent_eta ?? null, indent_note: r.indent_note ?? null,
  foto: Array.isArray(r.foto) ? (r.foto as string[]) : [],
  payments: (r.req_payments ?? []).map((p) => ({ amount: p.amount, method: p.method ?? undefined, note: p.note ?? undefined, by: p.by_user ?? undefined, created_at: p.created_at })),
});

export async function listRequests(ticketNo: string): Promise<SparepartReq[]> {
  const { data, error } = await supabase().from('sparepart_requests').select(REQ_SEL).eq('ticket_no', ticketNo).order('created_at');
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as ReqRaw[]).map(toReq);
}
export async function createRequest(d: { ticket_no: string; outlet: string | null; supplier_id: number | null; supplier_nama: string | null; items: string[]; by_user: string; indent?: boolean; foto?: string[] }): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').insert(d); if (error) throw new Error(error.message);
}
export async function serahRequest(id: number, serah: Serah): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').update({ serah }).eq('id', id); if (error) throw new Error(error.message);
}
export async function setBatalRequest(id: number, batal: boolean): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').update({ batal }).eq('id', id); if (error) throw new Error(error.message);
}
/** Retur: rekam jejak (serah+retur) & set batal dalam SATU update atomik → tak ada state "half-returned". */
export async function returAndBatal(id: number, serah: Serah): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').update({ serah, batal: true }).eq('id', id); if (error) throw new Error(error.message);
}

/** Kurangi stok sparepart via RPC atomik (SECURITY DEFINER) — jalan utk semua peran serah. */
export async function reduceStock(supplierId: number, barcode: string, qty: number): Promise<void> {
  const { error } = await supabase().rpc('decrement_stock', { p_supplier_id: supplierId, p_barcode: barcode, p_qty: qty });
  if (error) throw new Error(error.message);
}
/** Kembalikan stok (retur) = decrement negatif → stok naik. */
export const restoreStock = (supplierId: number, barcode: string, qty: number): Promise<void> => reduceStock(supplierId, barcode, -qty);

/** Hitung ulang modal tiket = Σ serah request non-batal (RPC SECURITY DEFINER) → tickets.modal.
 *  RPC (bukan JS) karena: (a) Supplier tak punya write_tickets; (b) listRequests RLS-filtered → JS salah scope. */
export async function recomputeTicketModal(ticketNo: string): Promise<void> {
  const { error } = await supabase().rpc('recompute_ticket_modal', { p_ticket_no: ticketNo });
  if (error) throw new Error(error.message);
}

// ---------- Hutang Supplier (payables) ----------
export interface Payable extends SparepartReq { ticketUnit: string; ticketHasil: string | null; ticketTeknisi: string | null }
type TicketRel = { unit: string | null; hasil: string | null; teknisi: string | null };
export async function listPayables(): Promise<Payable[]> {
  const data = await fetchAllPaged<unknown>((a, b) => supabase().from('sparepart_requests')
    // `diterima_at` WAJIB: hutang diakui saat teknisi mengonfirmasi menerima (keputusan Apo 30 Jul).
    // Kalau tidak disaring di sini, laporan hutang akan memuat barang yang belum tentu sampai —
    // sementara modal tiket sudah menunggu konfirmasi, jadi kedua angka tak lagi bertemu.
    .select(`${REQ_SEL},tickets(unit,hasil,teknisi)`).not('serah', 'is', null).not('diterima_at', 'is', null).eq('batal', false)
    .order('created_at', { ascending: false }).order('id').range(a, b)) as unknown as (ReqRaw & { tickets: TicketRel | TicketRel[] | null })[];
  return data.map((r) => {
    const tk = Array.isArray(r.tickets) ? r.tickets[0] : r.tickets;
    return { ...toReq(r), ticketUnit: tk?.unit ?? '—', ticketHasil: tk?.hasil ?? null, ticketTeknisi: tk?.teknisi ?? null };
  });
}
export async function addPayment(d: { req_id: number; outlet: string | null; amount: number; method: string; note: string; by_user: string }): Promise<void> {
  const { error } = await supabase().from('req_payments').insert(d); if (error) throw new Error(error.message);
}

// ---------- Laporan Pemakaian Sparepart (item-level) ----------
export interface UsageRow { ticket_no: string; teknisi: string; outlet: string | null; supplier_nama: string; barcode: string; nama: string; qty: number; harga: number; at: string }
export async function listUsage(): Promise<UsageRow[]> {
  const data = await fetchAllPaged<unknown>((a, b) =>
    supabase().from('sparepart_requests')
      .select('ticket_no,outlet,supplier_nama,serah,tickets(teknisi)')
      .not('serah', 'is', null).not('diterima_at', 'is', null).eq('batal', false).order('id').range(a, b)) as unknown as (ReqRaw & { tickets: { teknisi: string | null } | { teknisi: string | null }[] | null })[];
  const out: UsageRow[] = [];
  for (const r of data) {
    const serah = (r.serah ?? null) as Serah | null;
    const tek = (Array.isArray(r.tickets) ? r.tickets[0]?.teknisi : r.tickets?.teknisi) ?? '—';
    for (const it of serah?.items ?? []) out.push({ ticket_no: r.ticket_no, teknisi: tek, outlet: r.outlet, supplier_nama: r.supplier_nama ?? '—', barcode: it.barcode, nama: it.nama, qty: it.qty, harga: it.harga, at: serah?.at ?? r.created_at });
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

// ---------- Kerugian teknisi (retur DITOLAK supplier → part tetap dibayar iFiCs) ----------
export interface TekLoss { ticket_no: string; teknisi: string; outlet: string | null; supplier_nama: string; items: SerahItem[]; loss: number; reason: string; at: string; tglAmbilISO: string | null; arsipKeu: boolean }
type TekTicket = { teknisi: string | null; tgl_ambil_iso: string | null; arsip_keu: boolean | null };
/**
 * Semua retur yang DITOLAK supplier, per tiket/teknisi → kerugian (pengurang bonus). Derived dari serah.retur.
 * `.eq('batal', false)` WAJIB: request yang dibatalkan dikeluarkan dari modal/HPP (domain/sparepart) →
 * kalau tetap dihitung rugi, pemulihan diakui tanpa HPP pasangannya (laba fiktif). Samakan dgn supplierLedger.
 * tglAmbilISO + arsipKeu dibawa agar pemakai keuangan bisa ber-finScope (pemulihan hanya bila HPP ada di buku).
 */
export async function listRejectedReturns(): Promise<TekLoss[]> {
  const data = await fetchAllPaged<unknown>((a, b) =>
    supabase().from('sparepart_requests')
      .select('ticket_no,outlet,supplier_nama,serah,tickets(teknisi,tgl_ambil_iso,arsip_keu)')
      .not('serah', 'is', null).eq('batal', false).order('id').range(a, b)) as unknown as (ReqRaw & { tickets: TekTicket | TekTicket[] | null })[];
  const out: TekLoss[] = [];
  for (const r of data) {
    const serah = (r.serah ?? null) as Serah | null;
    const loss = rejectedLoss(serah);
    if (loss <= 0) continue;
    const t = (Array.isArray(r.tickets) ? r.tickets[0] : r.tickets) ?? null;
    const ev = (serah?.retur ?? []).find((x) => x.status === 'ditolak');
    out.push({ ticket_no: r.ticket_no, teknisi: t?.teknisi ?? '—', outlet: r.outlet, supplier_nama: r.supplier_nama ?? '—', items: ev?.items ?? [], loss, reason: ev?.ket ?? '—', at: ev?.decidedAt ?? ev?.at ?? r.created_at, tglAmbilISO: t?.tgl_ambil_iso ?? null, arsipKeu: !!t?.arsip_keu });
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

// ---------- Statement per supplier (ledger debit serah / kredit bayar) ----------
export interface LedgerRow { at: string; ket: string; debit: number; kredit: number }
export async function supplierLedger(supplierId: number): Promise<{ nama: string; rows: LedgerRow[] }> {
  const { data, error } = await supabase().from('sparepart_requests')
    .select(`${REQ_SEL},suppliers(nama)`).eq('supplier_id', supplierId).eq('batal', false).not('serah', 'is', null).not('diterima_at', 'is', null);
  if (error) throw new Error(error.message);
  const rows: LedgerRow[] = []; let nama = '—';
  for (const raw of (data ?? []) as unknown as (ReqRaw & { suppliers: { nama: string } | { nama: string }[] | null })[]) {
    nama = (Array.isArray(raw.suppliers) ? raw.suppliers[0]?.nama : raw.suppliers?.nama) ?? nama;
    const req = toReq(raw);
    const debit = (req.serah?.items ?? []).reduce((a, it) => a + it.qty * it.harga, 0);
    rows.push({ at: req.serah?.at ?? req.created_at, ket: `Serah part · ${req.ticket_no}`, debit, kredit: 0 });
    for (const p of req.payments ?? []) rows.push({ at: p.created_at ?? '', ket: `Pembayaran (${p.method ?? '-'})`, debit: 0, kredit: p.amount });
  }
  rows.sort((a, b) => (a.at < b.at ? -1 : 1));
  return { nama, rows };
}

export async function ticketInfo(no: string): Promise<{ status: string; teknisi: string | null }> {
  const { data, error } = await supabase().from('tickets').select('status,teknisi').eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as { status: string; teknisi: string | null };
  return { status: r.status, teknisi: r.teknisi };
}

/** Meta tiket utk SUPPLIER via RPC aman (no/unit/status/teknisi saja — tanpa kolom sensitif).
 *  Supplier tak punya read penuh tabel tickets (cegah bocor sandi/keluhan/biaya). */
export async function ticketMetaSupplier(no: string): Promise<{ status: string; teknisi: string | null; unit: string | null }> {
  const { data, error } = await supabase().rpc('supplier_ticket_meta', { p_ticket_no: no });
  if (error) throw new Error(error.message);
  const r = (data as { no: string; unit: string | null; status: string; teknisi: string | null }[] | null)?.[0];
  if (!r) throw new Error('Tiket tidak ditemukan / di luar akses supplier.');
  return { status: r.status, teknisi: r.teknisi, unit: r.unit };
}

/** Satu request + payments (utk cek re-serah & hitung ulang sisa hutang segar). */
export async function getRequest(id: number): Promise<SparepartReq | null> {
  const { data, error } = await supabase().from('sparepart_requests').select(REQ_SEL).eq('id', id).limit(1);
  if (error) throw new Error(error.message);
  const r = (data as unknown as ReqRaw[] | null)?.[0];
  return r ? toReq(r) : null;
}

/** Supplier menandai perkiraan tanggal barang indent tersedia (+ catatan). */
export async function setIndentEta(id: number, eta: string | null, note: string): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').update({ indent_eta: eta, indent_note: note || null }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Perbarui daftar foto request (dipakai saat menghapus salah satu foto). */
export async function setFotoRequest(id: number, foto: string[]): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').update({ foto }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Supplier menandai barang kosong → jadi pesanan indent + keterangan (request tak dibatalkan). */
export async function setIndentKosong(id: number, ket: string): Promise<void> {
  const { error } = await supabase().from('sparepart_requests').update({ indent: true, indent_note: ket }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Supplier menolak indent → request dibatalkan, alasan tersimpan sebagai keterangan. */
export async function tolakIndent(id: number, alasan: string): Promise<void> {
  const { error } = await supabase().from('sparepart_requests')
    .update({ batal: true, indent_note: `DITOLAK: ${alasan}` }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Konfirmasi penerimaan sparepart (RPC 0128) — modal & hutang diakui di titik ini. */
export async function terimaSparepart(reqId: number, ket = ''): Promise<void> {
  const { error } = await supabase().rpc('terima_sparepart', { p_req_id: reqId, p_ket: ket || null });
  if (error) throw new Error(error.message);
}

/**
 * Berapa supplier yang MENAGIH dan tagihannya belum tuntas → tanda 🔴 di menu & tab Hutang Supplier.
 * Tanpa penanda ini, tagihan supplier hanya lewat sebagai satu notifikasi di lonceng lalu terkubur,
 * dan Finance tak punya jejak visual siapa yang masih menunggu bayaran.
 *
 * "Menagih" = `suppliers.tagih_at` terisi (RPC 0118/0120) dan sejak itu masih ada nota belum lunas.
 * Nota yang dihitung hanya yang sudah DITERIMA teknisi — sama dengan basis pengakuan hutang (0128).
 */
export async function supplierMenagihCount(): Promise<number> {
  const [supRes, reqRes] = await Promise.all([
    supabase().from('suppliers').select('id,tagih_at').not('tagih_at', 'is', null),
    fetchAllPaged<{ supplier_id: number | null; serah: Serah | null; req_payments?: { amount: number }[] }>((a, b) =>
      supabase().from('sparepart_requests').select('supplier_id,serah,req_payments(amount)')
        .not('serah', 'is', null).not('diterima_at', 'is', null).eq('batal', false).order('id').range(a, b)).catch(() => []),
  ]);
  if (supRes.error) return 0;
  const menagih = (supRes.data ?? []) as { id: number; tagih_at: string }[];
  if (!menagih.length) return 0;
  const sisaPer = new Map<number, number>();
  for (const r of reqRes) {
    if (!r.supplier_id) continue;
    const sisa = Math.max(0, serahTotal(r.serah) - (r.req_payments ?? []).reduce((a, p) => a + (p.amount || 0), 0));
    if (sisa > 0) sisaPer.set(r.supplier_id, (sisaPer.get(r.supplier_id) ?? 0) + sisa);
  }
  return menagih.filter((s) => (sisaPer.get(s.id) ?? 0) > 0).length;
}

export interface MenungguTerima { req_id: number; ticket_no: string; outlet: string | null; teknisi: string | null; supplier_nama: string | null; jamLalu: number; nilai: number }
/**
 * Sparepart yang sudah DISERAHKAN supplier tapi belum dikonfirmasi diterima teknisi (0128).
 * Perlu tanda tersendiri: selama belum dikonfirmasi, modal tiket & hutang supplier BELUM diakui —
 * jadi barang yang tertahan di sini bukan cuma menunda pekerjaan, tapi juga membuat angka
 * keuangan tampak lebih kecil dari kenyataan.
 */
export async function listMenungguTerima(): Promise<MenungguTerima[]> {
  const data = await fetchAllPaged<unknown>((a, b) =>
    supabase().from('sparepart_requests')
      .select('id,ticket_no,outlet,supplier_nama,serah,tickets(teknisi)')
      .not('serah', 'is', null).is('diterima_at', null).eq('batal', false).order('id').range(a, b))
    .catch(() => []) as unknown as (ReqRaw & { tickets: { teknisi: string | null } | { teknisi: string | null }[] | null })[];
  const out: MenungguTerima[] = [];
  for (const r of data) {
    const serah = (r.serah ?? null) as Serah | null;
    const at = serah?.at ? Date.parse(serah.at) : Date.parse(r.created_at);
    out.push({
      req_id: r.id, ticket_no: r.ticket_no, outlet: r.outlet,
      teknisi: (Array.isArray(r.tickets) ? r.tickets[0]?.teknisi : r.tickets?.teknisi) ?? null,
      supplier_nama: r.supplier_nama ?? null,
      jamLalu: isNaN(at) ? 0 : Math.floor((Date.now() - at) / 3_600_000),
      nilai: serahTotal(serah),
    });
  }
  return out.sort((a, b) => b.jamLalu - a.jamLalu);
}
